import { useCallback, useState } from 'react'

import { Category } from '../../models/Category'
import { Item } from '../../models/Item'
import { Line } from '../../models/Line'
import { Payment } from '../../models/Payment'
import { Icon } from '../Icon'
import { PSApplication } from '../app/PSApplication'
import Card from '../app/design/Card'
import Divider from '../app/design/Divider'
import Heading from '../app/design/Heading'
import Label from '../app/design/Label'
import SegmentedControl from '../app/design/SegmentedControl'
import { ClientSearchableSelect } from '../contacts/ClientSearchableSelect'
import { TextArea } from '../common/fields/TextArea'
import { WorkflowPage } from '../workflows/WorkflowPage'
import { NewChargeCustomLineModal } from './NewChargeCustomLineModal'
import { NewChargeLineView } from './NewChargeLineView'
import { NewChargeItemsView } from './NewChargeItemsView'
import { NewChargeKeypad } from './NewChargeKeypad'
import { NewChargeReceiptPage } from './NewChargeReceiptPage'

export const POS_LIMIT = 25000.0

type Props = {
    payment: Payment
    taxable: boolean
    items: Item[]
    categories: Category[]
    onUpdatePayment: (payment: Payment) => void
    minimumAmount?: number
    maximumAmount?: number
    trueMaximumAmount?: number
    usesPaymentPlan: boolean
}

export function NewChargePage({
    payment,
    taxable,
    items,
    categories,
    onUpdatePayment,
    minimumAmount,
    maximumAmount,
    usesPaymentPlan,
    trueMaximumAmount,
}: Props) {
    const owner = PSApplication.owner()

    const [view, setView] = useState<'keypad' | 'items'>('keypad')
    const [customLineToEdit, setCustomLineToEdit] = useState<
        { line: Line; index: number } | undefined
    >(undefined)

    const handleKeypadSubmit = useCallback(
        (total: number, description?: string) => {
            const newLine = new Line({
                type: 'custom',
                item_display: 'Custom',
                total,
                memo: description,
                rate: total,
                quantity: 1,
                taxable,
                discountable: owner?.allowDiscount ?? false,
            })
            const clonedCharge = payment.clone({ lines: [...payment.lines, newLine] })
            clonedCharge.syncTotal()
            clonedCharge.syncSalesTax()
            onUpdatePayment(clonedCharge)
        },
        [taxable, owner?.allowDiscount, payment]
    )

    return (
        <>
            <div className="flex items-start justify-center gap-6">
                <Card size="lg" className="w-[744px] max-w-[744px] rounded-lg bg-white p-8">
                    <div className="flex justify-between">
                        <Heading size="sm" className="capitalize">
                            {view}
                        </Heading>
                        {!usesPaymentPlan && (
                            <SegmentedControl
                                size="small"
                                segments={[
                                    {
                                        label: 'Keypad',
                                        value: 'keypad',
                                        icon: <Icon icon="calculator.small" shouldInheritColor />,
                                    },
                                    {
                                        label: 'Items',
                                        value: 'items',
                                        icon: <Icon icon="box.small" shouldInheritColor />,
                                    },
                                ]}
                                value={view}
                                onChange={(value) => setView(value as 'keypad' | 'items')}
                            />
                        )}
                    </div>

                    {view === 'keypad' && (
                        <NewChargeKeypad
                            onSubmit={handleKeypadSubmit}
                            min={minimumAmount ?? 0}
                            max={maximumAmount ?? POS_LIMIT}
                            showPaymentLimits={usesPaymentPlan}
                        />
                    )}
                    {view === 'items' && (
                        <NewChargeItemsView
                            items={items}
                            categories={categories}
                            onSelect={(item) => {
                                const clonedPayment = payment.clone({
                                    lines: [...payment.lines, Line.initWithItem(item, payment)],
                                })
                                clonedPayment.syncTotal()
                                clonedPayment.syncSalesTax()
                                onUpdatePayment(clonedPayment)
                            }}
                        />
                    )}
                </Card>

                <div className="flex w-[360px] flex-col gap-6">
                    <Card size="sm">
                        <Heading size="xs" className="mb-6">
                            Client
                        </Heading>
                        <ClientSearchableSelect
                            mode="server"
                            selectedClient={payment.client}
                            selectedSubaccount={payment.subaccount}
                            onSelectClient={(client) => {
                                onUpdatePayment(payment.clone({ client }))
                            }}
                            onSelectSubaccount={(subaccount) => {
                                onUpdatePayment(
                                    payment.clone({ client: subaccount?.client, subaccount })
                                )
                            }}
                            shouldPreserveLastSelection
                            shouldIncludeSubaccounts
                        />
                    </Card>
                    <Card size="sm">
                        <Heading size="xs" className="mb-6">
                            Charge summary
                        </Heading>
                        {payment.lines.length > 0 && (
                            <div className="flex flex-col gap-4">
                                {payment.lines.map((line, i) => (
                                    <NewChargeLineView
                                        isEditable={!usesPaymentPlan}
                                        key={`${line.id}-${i}-${line.rate}`}
                                        max={maximumAmount ?? POS_LIMIT}
                                        line={line}
                                        onQuickEdit={(editedLine) => {
                                            const clonedPayment = payment.clone({
                                                lines: payment.lines.map((l, li) =>
                                                    li === i ? editedLine : l
                                                ),
                                            })
                                            clonedPayment.syncTotal()
                                            clonedPayment.syncSalesTax()
                                            onUpdatePayment(clonedPayment)
                                        }}
                                        onEdit={() => setCustomLineToEdit({ line, index: i })}
                                        onRemove={() => {
                                            const clonedPayment = payment.clone({
                                                lines: payment.lines.filter((_, li) => li !== i),
                                            })
                                            clonedPayment.syncTotal()
                                            clonedPayment.syncSalesTax()
                                            onUpdatePayment(clonedPayment)
                                        }}
                                    />
                                ))}
                            </div>
                        )}
                        {payment.lines.length > 0 && <Divider className="mt-4 mb-[10px]" />}
                        <WorkflowPage.ReceiptView
                            owner={owner}
                            transaction={payment}
                            modifierGapSize="small"
                            onUpdateTransaction={
                                usesPaymentPlan
                                    ? undefined
                                    : (t) => {
                                          onUpdatePayment(t)
                                      }
                            }
                            dividerClassName="my-[10px]"
                            receipt={{
                                totalText: 'Total',
                                total: payment.chargeAmount ?? 0,
                                max: trueMaximumAmount ?? POS_LIMIT,
                                modifiers:
                                    payment.lines.length === 0
                                        ? undefined
                                        : {
                                              deleteButtonMargin: '-right-14',
                                              subtotal: {
                                                  amount: payment.total ?? 0,
                                              },
                                              salesTax:
                                                  usesPaymentPlan && !payment.tax
                                                      ? undefined
                                                      : {
                                                            amount: payment.taxAmount ?? 0,
                                                            parenthetical: '',
                                                        },
                                              surcharge: {
                                                  amount: payment.surchargeAmount ?? null,
                                                  onRemove: usesPaymentPlan
                                                      ? undefined
                                                      : () => {
                                                            onUpdatePayment(
                                                                payment.clone({
                                                                    allow_surcharge: false,
                                                                    taxable_amount: 0,
                                                                })
                                                            )
                                                        },
                                                  onAdd: usesPaymentPlan
                                                      ? undefined
                                                      : () => {
                                                            onUpdatePayment(
                                                                payment.clone({
                                                                    allow_surcharge: true,
                                                                })
                                                            )
                                                        },
                                              },
                                              discount: usesPaymentPlan ? undefined : {}, // should not be able to discount a package when there are payments
                                              deposit: Boolean(payment.paidDeposit)
                                                  ? {
                                                        amount: payment.paidDeposit ?? 0,
                                                        title: 'Paid deposit',
                                                        isPaid: true,
                                                    }
                                                  : undefined,
                                          },
                            }}
                        />
                        {payment.total !== undefined && payment.total > 0 && (
                            <>
                                <Divider className="my-4" />
                                <div className="flex gap-4">
                                    <Icon icon="note.large" shouldInheritColor />
                                    <div className="flex grow flex-col gap-3">
                                        <Label size="md">Receipt memo</Label>
                                        <TextArea
                                            value={payment.memo}
                                            isAutoResizing
                                            placeholder="Enter a receipt memo"
                                            onChange={(e) => {
                                                onUpdatePayment(
                                                    payment.clone({ memo: e.target.value })
                                                )
                                            }}
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                    </Card>
                    {usesPaymentPlan && (
                        <NewChargeReceiptPage
                            trandoc={
                                payment.type === 'invoice' ? payment.invoice : payment.aPackage
                            }
                        />
                    )}
                </div>
            </div>
            <NewChargeCustomLineModal
                line={customLineToEdit?.line}
                key={customLineToEdit?.index}
                onClose={() => setCustomLineToEdit(undefined)}
                onSave={(editedLine) => {
                    const clonedPayment = payment.clone({
                        lines: payment.lines.map((line, i) =>
                            i === customLineToEdit?.index ? editedLine : line
                        ),
                    })
                    clonedPayment.syncTotal()
                    clonedPayment.syncSalesTax()
                    onUpdatePayment(clonedPayment)
                    setCustomLineToEdit(undefined)
                }}
            />
        </>
    )
}
